<script setup lang="ts">
// The app's shared header + menu. Until v0.67.0 this markup lived inside
// pages/index.vue, so the ONLY page with a menu was the home page and every
// other page hand-rolled a two-link header — which is why identity
// verification and the cashier were effectively unreachable for a player who
// never opened a contest (CHANGELOG v0.60.0).
//
// The menu is a two-level tree (MENU below). A primary is EITHER a link OR a
// group label with children — never both (v0.76.x: a primary that navigated
// AND dropped a submenu read as ambiguous), so every destination is a leaf.
// Desktop renders the primaries inline in .bankroll (`display: contents`);
// group labels open a small hover/click submenu panel. Below the breakpoint
// everything collapses into the hamburger dropdown as one grouped list: group
// labels become non-interactive section eyebrows, children sit in a railed
// tinted block, hairline dividers fence each group (no popovers — the native
// embed and phones get one flat scrollable column). One markup, both layouts,
// and the native embed uses the same thing — so a link added here lands on
// all three delivery targets at once.
//
// `crumbs` renders a "parent / current" trail in the .dom title area — the one
// "up" affordance on level-2+ pages. The parent segment is a real link whose
// href carries context (works from a deep link / new tab), but a plain
// left-click prefers router.back() when the previous history entry IS that
// parent, so its full ?sport=&tab= state is restored.
//
// navState reporting lives HERE (not per page): the menu state is folded with
// the host page's own overlays via the `overlayOpen` prop. The OS-back
// interceptor and the scrim are owned here too. Registering in a child means
// this interceptor sits at the BOTTOM of the bridge's back stack, which runs
// newest-first — so page overlays are still offered the back event before the
// menu, exactly as when index.vue registered these in order.
interface Crumb { label: string; to?: string }
interface MenuItem { label: string; to?: string; children?: { label: string; to: string }[] }

const props = withDefaults(defineProps<{
  title?: string
  crumbs?: Crumb[]
  overlayOpen?: boolean
}>(), { title: 'ProBook' })
const open = defineModel<boolean>('open', { default: false })

const { user, logout } = useAuth()
const toast = useToast()
const bridge = useNativeBridge()
const route = useRoute()
const router = useRouter()

// A parent with children is a GROUP LABEL, never a link — one control, one
// behavior (a primary that both navigated and dropped a submenu read as
// ambiguous). Every destination is a leaf: the dfs lobby lives under its group
// as "contests", the wallet under "account".
const MENU: MenuItem[] = [
  {
    label: 'dfs',
    children: [
      { label: 'contests', to: '/dfs' },
      { label: 'lineups', to: '/dfs/tickets' },
    ],
  },
  { label: 'scores', to: '/scoreboard' },
  { label: 'leaders', to: '/leaderboards' },
  { label: 'bets', to: '/bets' },
  {
    label: 'account',
    children: [
      { label: 'wallet', to: '/wallet' },
      { label: 'transactions', to: '/wallet/transactions' },
      { label: 'deposit limits', to: '/account/deposit-limits' },
      { label: 'responsible gaming', to: '/account/responsible-gaming' },
    ],
  },
]

// cash-only destinations drop out of the tree while the platform profile says
// under 18 (cash_age_eligible === false): the cashier and deposit limits have
// nothing to offer a player the cashier is closed to. Transactions stay (BMO
// history lives there too) and responsible gaming applies to free play.
// With CASH_ENABLED=0 the whole cash product is off, and responsible gaming
// goes with it: its tools (deposit limits, self-exclusion) exist for cash play
// and the page redirects home (middleware/cash.ts) — a leaf must never lead to
// a redirect. Transactions is the one account leaf left.
const { cashEnabled } = useFeatures()
const CASH_ONLY = new Set(['/wallet', '/account/deposit-limits'])
const CASH_PRODUCT = new Set([...CASH_ONLY, '/account/responsible-gaming'])
const menu = computed<MenuItem[]>(() => {
  const drop = !cashEnabled.value ? CASH_PRODUCT
    : user.value?.cash_age_eligible === false ? CASH_ONLY : null
  if (!drop) return MENU
  return MENU.map(i => i.children ? { ...i, children: i.children.filter(k => !drop.has(k.to)) } : i)
})

const subOpen = ref<string | null>(null)
function toggleSub(label: string) { subOpen.value = subOpen.value === label ? null : label }
function close() { open.value = false; subOpen.value = null }

// public pages: send anonymous users to sign-in and bring them back here
const signInTo = computed(() => `/login?redirect=${encodeURIComponent(route.fullPath)}`)

async function doLogout() {
  close()
  try { await logout() } catch { toast.error('logout failed') }
}

// crumb smart-back: prefer history when the previous entry is the crumb's
// target page, so the parent's query state (league, tab) is restored rather
// than replaced by the fallback href. Modified clicks keep link semantics.
function crumbGo(e: MouseEvent, c: Crumb) {
  if (!c.to || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
  const prev = (window.history.state as any)?.back
  if (typeof prev === 'string' && prev.split('?')[0] === c.to.split('?')[0]) {
    e.preventDefault()
    router.back()
  }
}

onMounted(() => {
  // desktop submenu hygiene: outside click / escape close it
  const onDocClick = (e: MouseEvent) => {
    if (subOpen.value && !(e.target as Element | null)?.closest?.('.nav-item')) subOpen.value = null
  }
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
  document.addEventListener('click', onDocClick)
  document.addEventListener('keydown', onKey)
  onBeforeUnmount(() => {
    document.removeEventListener('click', onDocClick)
    document.removeEventListener('keydown', onKey)
  })

  if (!bridge.isNative.value) return
  const off = bridge.registerBack(() => {
    if (open.value) { close(); return true }
    return false
  })
  const anyOverlay = () => open.value || !!props.overlayOpen
  const stop = watch([open, () => props.overlayOpen], () => bridge.navState({
    canGoBack: window.history.length > 1,
    overlayOpen: anyOverlay(),
  }), { immediate: true })
  onBeforeUnmount(() => { off(); stop() })
})
</script>

<template>
  <header class="top">
    <div class="brand">
      <NuxtLink to="/" class="brand-home"><img src="/BuzztimeLogo.webp" class="logo" alt="Buzztime" /></NuxtLink>
      <span v-if="crumbs?.length" class="dom">
        <template v-for="(c, i) in crumbs" :key="i">
          <NuxtLink v-if="c.to" :to="c.to" class="crumb" @click="crumbGo($event, c)">{{ c.label }}</NuxtLink>
          <span v-else class="crumb-here">{{ c.label }}</span>
          <span v-if="i < crumbs.length - 1" class="crumb-sep">/</span>
        </template>
      </span>
      <span v-else class="dom">{{ props.title }}</span>
    </div>
    <div class="bankroll">
      <slot name="status"><BalanceBadge /></slot>
      <button class="hamburger btn-ghost" :class="{ open }" aria-label="menu"
        :aria-expanded="open" @click="open = !open">
        <span class="ham-bars"><i></i><i></i><i></i></span>
      </button>
      <!-- one click handler closes the menu after any link/action; caret and
           group-label stop propagation so toggling a submenu never closes -->
      <div class="nav-actions" :class="{ open }" @click="close">
        <div v-for="item in menu" :key="item.label" class="nav-item"
          :class="{ 'has-kids': !!item.children, 'sub-open': subOpen === item.label }">
          <NuxtLink v-if="item.to" class="btn-ghost" :to="item.to">{{ item.label }}</NuxtLink>
          <button v-else class="btn-ghost group-label" aria-haspopup="true"
            :aria-expanded="subOpen === item.label" @click.stop="toggleSub(item.label)">
            {{ item.label }}<span class="gcaret" aria-hidden="true">▾</span></button>
          <div v-if="item.children" class="subnav">
            <NuxtLink v-for="kid in item.children" :key="kid.to" class="btn-ghost sub-link" :to="kid.to">
              {{ kid.label }}</NuxtLink>
          </div>
        </div>
        <slot name="extra" />
        <NuxtLink v-if="user?.is_admin || user?.is_support" class="btn-ghost" to="/admin">admin</NuxtLink>
        <button v-if="user && !bridge.isNative.value" class="btn-ghost" @click="doLogout">sign out</button>
        <NuxtLink v-else-if="!user && !bridge.isNative.value" class="btn-ghost" :to="signInTo">sign in</NuxtLink>
      </div>
    </div>
  </header>
  <!-- scrim lives outside <header> on purpose: the header's backdrop-filter
       would otherwise contain this position:fixed element to the header box. -->
  <div v-if="open" class="menu-scrim" @click="close"></div>
</template>

<style scoped>
/* On a wide desktop the primaries flow inline in .bankroll (display:contents
   makes .nav-actions a transparent wrapper) and the hamburger and scrim are
   hidden; children live in per-item submenu panels. */
.hamburger { display: none; }
.nav-actions { display: contents; }
.menu-scrim { display: none; }
.ham-bars { display: inline-flex; flex-direction: column; gap: 4px; }
.ham-bars i { display: block; width: 18px; height: 2px; border-radius: 2px; background: var(--ink); transition: transform .18s, opacity .18s; }
.hamburger.open .ham-bars i:nth-child(1) { transform: translateY(6px) rotate(45deg); }
.hamburger.open .ham-bars i:nth-child(2) { opacity: 0; }
.hamburger.open .ham-bars i:nth-child(3) { transform: translateY(-6px) rotate(-45deg); }

.nav-item { position: relative; display: inline-flex; align-items: center; gap: 0; }
.gcaret { font-size: 9px; margin-left: 5px; opacity: .7; display: inline-block; transition: transform .15s; }
.nav-item.sub-open .gcaret { transform: rotate(180deg); }
/* hover bridge: keeps :hover alive while the pointer crosses the gap down to
   the submenu panel */
.nav-item.has-kids::after { content: ''; position: absolute; top: 100%; left: 0; right: 0; height: 10px; }
.subnav {
  display: none; position: absolute; top: calc(100% + 8px); left: 0; z-index: 40;
  flex-direction: column; gap: 6px; min-width: 176px; padding: 8px;
  background: var(--panel); border: 1px solid var(--line); border-radius: 12px;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.55);
}
.nav-item:hover > .subnav,
.nav-item:focus-within > .subnav,
.nav-item.sub-open > .subnav { display: flex; }
.subnav .sub-link { width: 100%; justify-content: flex-start; }

/* crumbs render inside .dom so they inherit its mono/uppercase treatment */
.crumb { opacity: .85; }
.crumb:hover { opacity: 1; text-decoration: underline; }
.crumb-sep { opacity: .5; margin: 0 6px; }
.crumb-here { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 34vw; display: inline-block; vertical-align: bottom; }

/* The primary row carries 6 items now (it was 12 flat), but the breakpoint
   stays at 1200px so tablet layout is unchanged (lowering it back toward the
   old 900px is a separate, deliberate layout change). */
@media (max-width: 1200px) {
  .bankroll { position: relative; gap: 12px; }
  .hamburger { display: inline-flex; align-items: center; justify-content: center; padding: 9px 11px; }
  .nav-actions {
    display: none; position: absolute; top: calc(100% + 10px); right: 0; z-index: 40;
    flex-direction: column; gap: 6px; min-width: 208px; padding: 8px;
    /* the list can outgrow a short viewport (13 rows in landscape) — scroll it
       rather than letting the tail fall off the bottom of the screen */
    max-height: min(70vh, 520px); overflow-y: auto;
    background: var(--panel); border: 1px solid var(--line); border-radius: 12px;
    box-shadow: 0 18px 44px rgba(0, 0, 0, 0.55);
  }
  .nav-actions.open { display: flex; }
  /* grouped flat list: children always visible. The group label reads as a
     SECTION HEADER (small-caps eyebrow, non-interactive), its children sit in
     a railed, tinted block, and each group is fenced by hairline dividers —
     the nesting is delineated, not merely indented. */
  .nav-item { display: flex; flex-direction: column; align-items: stretch; gap: 4px; width: 100%; }
  .nav-item.has-kids { border-bottom: 1px solid var(--line); padding-bottom: 8px; }
  .nav-item.has-kids:not(:first-child) { border-top: 1px solid var(--line); padding-top: 8px; }
  .gcaret { display: none; }
  .nav-actions .group-label {
    pointer-events: none; width: 100%;
    font-family: var(--font-mono); font-size: 10px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.12em; color: var(--muted);
    padding: 4px 10px 2px;
  }
  .nav-item.has-kids::after { display: none; }
  .subnav {
    display: flex; position: static; min-width: 0; gap: 4px;
    margin: 0 2px 2px 10px; padding: 4px 4px 4px 8px;
    border: none; border-left: 2px solid var(--line);
    background: rgba(255, 255, 255, 0.04);
    border-radius: 0 10px 10px 0; box-shadow: none;
  }
  /* :deep so the rule also reaches #extra slot content, which carries the HOST
     page's scope id, not this component's */
  .nav-actions :deep(.btn-ghost) { width: 100%; justify-content: flex-start; }
  .menu-scrim { display: block; position: fixed; inset: 0; z-index: 18; }
}
</style>
